import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createManagementReviewRegisterItemApi,
  decideEmsApprovalApi,
  deleteManagementReviewRegisterItemApi,
  downloadManagementReviewRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchManagementReviewRegisterApi,
  requestEmsApprovalApi,
  updateManagementReviewRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "management_reviews";

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  meeting_date: "",
  chair: "",
  attendees: "",
  agenda: "",
  decisions: "",
  actions: "",
  follow_up_date: "",
  status: "planned",
  evidence: "",
  notes: "",
};

export default function ManagementReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterChair, setFilterChair] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchManagementReviewRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load management reviews");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("9");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "9" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Title is required.");
    if (!String(form.meeting_date || "").trim()) errs.push("Meeting date is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "closed") {
      if (!String(form.chair || "").trim()) errs.push("Chair is required before setting status to closed.");
      if (!String(form.attendees || "").trim()) errs.push("Attendees are required before setting status to closed.");
      if (!String(form.agenda || "").trim()) errs.push("Agenda is required before setting status to closed.");
      if (!String(form.decisions || "").trim()) errs.push("Decisions are required before setting status to closed.");
      if (!String(form.actions || "").trim()) errs.push("Actions are required before setting status to closed.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to closed.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("management_reviews");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["closed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      meeting_date: item.meeting_date || "",
      chair: item.chair || "",
      attendees: item.attendees || "",
      agenda: item.agenda || "",
      decisions: item.decisions || "",
      actions: item.actions || "",
      follow_up_date: item.follow_up_date || "",
      status: item.status || "planned",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateManagementReviewRegisterItemApi(editingId, form);
        toast.success("Management review updated");
      } else {
        await createManagementReviewRegisterItemApi(form);
        toast.success("Management review created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save management review");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this management review entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteManagementReviewRegisterItemApi(id);
      toast.success("Management review deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete management review");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadManagementReviewRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "management_review_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Management review register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export management review register");
    } finally {
      setExporting(false);
    }
  };

  const chairOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.chair || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterChair === "__unassigned__") {
        if (String(it.chair || "").trim()) return false;
      } else if (filterChair !== "all" && String(it.chair || "") !== filterChair) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.follow_up_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.follow_up_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterChair, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "planned_only") {
      setFilterStatus("planned");
      setFilterChair("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "actions_in_progress") {
      setFilterStatus("actions_in_progress");
      setFilterChair("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterChair("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    setFilterStatus("all");
    setFilterChair("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 performance evaluation</p>
          <h1>Management Reviews</h1>
          <p className="subtitle">Record top-management review of EMS performance, decisions and follow-up actions in one auditable record.</p>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to complete a review</p>
          <h3>Record what management considered, decided and will do next</h3>
          <p>Create one record per formal management review. The final record should show who attended, the EMS performance information considered, the decisions made and the actions that follow.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Prepare</strong><span>Set the meeting date, chair and attendees, then list the required EMS inputs on the agenda.</span></div>
          <div><strong>Decide</strong><span>Record clear decisions on performance, resources, changes, risks, opportunities and improvement.</span></div>
          <div><strong>Follow through</strong><span>Assign actions, set a follow-up date, and link signed minutes or other meeting evidence.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header"><div><p className="guided-register-eyebrow">Your management review</p><h3>{editingId ? "Edit Review" : "Add Review"}</h3></div><span className="kpi-sub">Keep the agenda, decisions, actions and supporting evidence together for management and audit review.</span></div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Annual EMS management review - 2026" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Use a clear meeting name that identifies its purpose and period.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="planned">planned</option>
                <option value="held">held</option>
                <option value="actions_in_progress">actions_in_progress</option>
                <option value="closed">closed</option>
              </select>
              <small>Use planned before the meeting, held once it has taken place, and closed when decisions and actions are complete.</small>
            </label>
            <label className="settings-field">
              <span>Meeting date</span>
              <input type="date" value={form.meeting_date} onChange={(e) => setField("meeting_date", e.target.value)} disabled={saving} />
              <small>Record the planned or actual date of the management review meeting.</small>
            </label>
            <label className="settings-field">
              <span>Chair</span>
              <input placeholder="e.g. Managing Director" value={form.chair} onChange={(e) => setField("chair", e.target.value)} disabled={saving} />
              <small>Name the person responsible for leading the review.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Attendees</span>
              <input placeholder="e.g. Managing Director, Operations Manager, EMS Coordinator" value={form.attendees} onChange={(e) => setField("attendees", e.target.value)} disabled={saving} />
              <small>List everyone who attended or submitted input, including roles where helpful.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Agenda</span>
              <input placeholder="e.g. Context and environmental-condition changes, interested parties, climate relevance, audits, compliance, objectives, performance, risks, resources and improvement opportunities" value={form.agenda} onChange={(e) => setField("agenda", e.target.value)} disabled={saving} />
              <small>Cover the ISO 14001 management-review inputs relevant to your organisation, including Context changes and their effect on strategy, planning and resources.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Decisions</span>
              <input placeholder="e.g. Approve energy-reduction target and resource for LED upgrade" value={form.decisions} onChange={(e) => setField("decisions", e.target.value)} disabled={saving} />
              <small>Record specific management decisions, including approved changes, priorities or resources.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Actions</span>
              <input placeholder="e.g. Facilities Manager to obtain LED quotes by 31 October" value={form.actions} onChange={(e) => setField("actions", e.target.value)} disabled={saving} />
              <small>State each follow-up action with an owner and deadline; link it to Actions & Deadlines where needed.</small>
            </label>
            <label className="settings-field">
              <span>Follow-up date</span>
              <input type="date" value={form.follow_up_date} onChange={(e) => setField("follow_up_date", e.target.value)} disabled={saving} />
              <small>Set the next date for checking that review actions have been completed.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link signed minutes, presentations, performance reports, action logs or other evidence considered at the review."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record apologies, matters deferred, rationale for decisions or additional context." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Use this space for supporting context that does not replace a decision or action.</small>
            </label>
          </div>
          <div><strong>Reassess context</strong><span>Consider changes in environmental conditions, interested parties, climate relevance, strategic direction and material business decisions.</span></div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update review" : "Add review"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="planned">planned</option>
            <option value="held">held</option>
            <option value="actions_in_progress">actions_in_progress</option>
            <option value="closed">closed</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Chair</span>
          <select value={filterChair} onChange={(e) => setFilterChair(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {chairOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue follow-up only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("planned_only")}>Only planned</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("actions_in_progress")}>Actions in progress</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Follow-up due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Title</th>
              <th>Meeting date</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Chair</th>
              <th>Follow-up date</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={12} className="table-empty">No management review entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.meeting_date || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.chair || "-"}</td>
                  <td>{it.follow_up_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



